import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useContext,
} from 'react';
import { debounce } from 'lodash';
import NotificationContext from '../../../../providers/NotificationProvider';
import {
  showSuccessToast,
  showErrorToast,
} from '../../../../components/commonComponents/gxAntdToast/toastUtils';
import {
  Editor,
  EditorState,
  Modifier,
  RichUtils,
  convertToRaw,
  getDefaultKeyBinding,
} from 'draft-js';
import { draftToMarkdown } from 'markdown-draft-js';
import SendInbox from '../../../../assets/customSVG/inbox/SendInbox';
import { ICONS } from '../../../../assets/icons';
import Loader from '../../../../components/commonComponents/Loader/Index';

// assets
import Delete from '../../../../assets/customSVG/Delete';
import DocSvg from '../../../../assets/customSVG/DocSvg';
import ImageSvg from '../../../../assets/customSVG/ImageSvg';
import VideoSvg from '../../../../assets/customSVG/VideoSvg';
import '../../../../components/commonComponents/editor/editor.css';
import '../../styles/message_input.css';

// utils
import { Drawer, Modal, Popover } from 'antd';

// hooks
import useToggle from '../../../../hooks/useToggle';

// redux
import { cn } from '../../../../helper/cn';
import { updateToggleToast } from '../../../../reduxToolkit/appSlice';
import { markMessagesAsRead } from '../../api/Api';

import {
  useAspDispatch,
  useAspSelector,
} from '../../../../test/jest-redux-hooks';

// icons
import InboxNoteIcon from '../../../../assets/customSVG/inbox/InboxNoteIcon';

// components
import LightningIcon from '../../../../assets/customSVG/LightningIcon';
import InlineStyleControls from '../../../../components/commonComponents/editor/InlineStyleControls';
import Header from '../../../../components/commonComponents/modals/Header';
import TemplateListContainer from '../../../../components/commonComponents/templates/TemplateListContainer';
import NoteEditor from './NoteEditor';
import QuickReplyPanel from './QuickReplyPanel';

// Feature Gating
import { useFunction } from '../../../../hooks/useFeatureGating';
import { FUNCTIONS } from '../../../../constant/featureSlugs';
import { Tooltip } from 'antd';
import UpgradeBadge from '../../../../components/commonComponents/UpgradeBadge/UpgradeBadge';

// helper
import { uploadFile } from '../../../../helper/uploadFile';
import { replaceFormating } from '../../helper/index';

// contents
import {
  FEATURE_FLAG_SEND_ATTACHMENTS,
  FEATURE_FLAG_SEND_DOCUMENT,
  FEATURE_FLAG_SEND_IMAGE,
  FEATURE_FLAG_SEND_VIDEO,
  FEATURE_FLAG_TEMPLATE_CHANGE_MEDIA,
  FEATURE_FLAG_TEMPLATE_CHANGE_VARIABLE,
} from '../../constants/FeatureFlag';
import { INLINE_STYLES, mediaIcons } from '../../constants/index';
import { MESSAGE_TYPES } from '../../constants/index';

// API
import {
  createNote,
  inboxQuickReplies,
  sendTemplateMessage,
  sendTextMessage,
  updateNote,
} from '../../api/Api';

import { useNote } from '../../providers/NoteContext';

const MessageInput = React.memo(
  ({
    activeTabItem,
    selectedPhoneNumber,
    selectedPerson,
    conversationType,
    isChatResolved,
    chatToggle,
    selectedData,
    selectedFile: mediaAttachments = [],
    setSelectedFile: setMediaAttachments,
    isUploading,
    setIsUploading,
    handleChat,
    showList,
    setShowList,
    openTemplateList,
    setOpenTemplateList,
    assignGetData,
    setMembersTrue,
    getConversations,
    selectedMessage,
    setSelectedMessage,
    item,
    getNotes,
    setSelectedData,
    getAttachments,
    attachmentFilter,
    scrollingBottom,
    ccChatbotStatus,
  }) => {
    const editorRef = useRef(null);
    const textareaRef = useRef(null);
    const fileInputRef1 = useRef();
    const fileInputRef2 = useRef();
    const fileInputRef3 = useRef();
    const fileInputRef4 = useRef();

    const prevCountRef = useRef(0);

    const dispatch = useAspDispatch();
    const notification = useContext(NotificationContext);

    const { currentBrand, toggleToast } = useAspSelector((state) => state.app);

    // styles
    const countryCode =
      selectedPerson?.phone_country_code?.replace('+', '') || '';

    // hooks
    const [showQuickRepliesManager, setShowQuickRepliesManager] =
      useToggle(false);
    const [quickReplies, setQuickReplies] = useState([]);
    const [quickRepliesLoading, setQuickRepliesLoading] = useState(false);
    const [showToolbar, setShowToolbar] = useToggle(false);

    // editor
    const [editorState, setEditorState] = useState(EditorState.createEmpty());
    const [showEmojiPickerCaption, setShowEmojiPickerCaption] =
      useToggle(false);
    const [showEmojiPicker, setShowEmojiPicker] = useToggle(false);
    const [msg, setMsg] = useState('');
    const [fileOpen, setFileOpen] = useState(false);
    const [openAttachment, setOpenAttachment] = useToggle(false);
    const [captionEmojiTargetId, setCaptionEmojiTargetId] = useState(null);
    const [activeAttachmentId, setActiveAttachmentId] = useState(null);
    const [lockedAttachmentType, setLockedAttachmentType] = useState(null);

    // templates
    const [isSending, setIsSending] = useToggle(false);
    const [isMsgSending, setIsMsgSending] = useToggle(false);

    // Feature Gating
    const { isEnabled: canSendNotes } = useFunction(FUNCTIONS.INBOX_NOTES);
    const { isEnabled: canUseQuickReply } = useFunction(
      FUNCTIONS.INBOX_QUICK_REPLY,
    );
    const { isEnabled: canSendAudio } = useFunction(FUNCTIONS.INBOX_SEND_AUDIO);
    const { isEnabled: canSendDocument } = useFunction(
      FUNCTIONS.INBOX_SEND_DOC,
    );
    const { isEnabled: canSendImage } = useFunction(FUNCTIONS.INBOX_SEND_PHOTO);
    const { isEnabled: canSendVideo } = useFunction(FUNCTIONS.INBOX_SEND_VIDEO);

    // notes
    const { noteContent, setNoteContent } = useNote();
    const [isNoteLoading, setIsNoteLoading] = useState(false);

    let page = 1;
    const sizeLimitByType = {
      image: 5 * 1024 * 1024,
      video: 16 * 1024 * 1024,
      document: 100 * 1024 * 1024,
      audio: 16 * 1024 * 1024,
    };
    const sizeErrorMessage = {
      image: 'Image size should be less than 5MB',
      video: 'Video size should be less than 16MB',
      document: 'Document size should be less than 100MB',
      audio: 'Audio size should be less than 16MB',
    };
    const MAX_ATTACHMENTS = 10;
    const attachments = mediaAttachments || [];
    const hasAttachmentError = attachments?.some((item) =>
      Boolean(item?.error),
    );
    const activeAttachment = useMemo(
      () => attachments?.find((att) => att?.id === activeAttachmentId) || null,
      [attachments, activeAttachmentId],
    );

    const updateAttachment = useCallback(
      (attachmentId, payload) => {
        setMediaAttachments(
          (prev) =>
            prev?.map((item) =>
              item?.id === attachmentId
                ? {
                    ...item,
                    ...(typeof payload === 'function'
                      ? payload(item)
                      : payload),
                  }
                : item,
            ) || [],
        );
      },
      [setMediaAttachments],
    );

    const renderPrimaryPreview = useCallback((attachment) => {
      if (!attachment) return null;
      const previewSource = attachment?.preview || attachment?.url;
      const previewClasses =
        'w-[28vw] h-[22vw] max-w-[32vw] rounded-[1vw] object-contain bg-white';

      if (attachment?.type === 'image' && previewSource) {
        return (
          <img src={previewSource} alt="preview" className={previewClasses} />
        );
      }

      if (attachment?.type === 'video' && previewSource) {
        const videoType = attachment?.file?.type || 'video/mp4';
        return (
          <video
            key={`${attachment?.id}-${previewSource}`}
            className={`${previewClasses} flex items-center justify-center`}
            controls
          >
            <source src={previewSource} type={videoType} />
          </video>
        );
      }

      if (attachment?.type === 'audio' && previewSource) {
        const audioType = attachment?.file?.type || 'audio/mpeg';
        return (
          <audio
            key={`${attachment?.id}-${previewSource}`}
            className="w-[22vw] max-w-[32vw]"
            controls
          >
            <source src={previewSource} type={audioType} />
          </audio>
        );
      }

      if (attachment?.type === 'document' && previewSource) {
        return (
          <>
            <iframe
              className="w-[400px] h-[300px] object-cover rounded-lg ml-10 mr-10 flex-shrink-0"
              src={previewSource}
              title="Document preview"
            />
          </>
        );
      }

      return (
        <div className="w-[22vw] h-[22vw] max-w-[32vw] bg-[#F6F7FA] rounded-[1vw] flex flex-col items-center justify-center gap-2">
          <DocSvg width={'4vw'} height={'4vw'} color="var(--textBlack)" />
          <p className="text-[0.9rem] text-[var(--font-600)]">
            No preview available
          </p>
          <p className="text-[0.75rem] text-[var(--font-400)] uppercase">
            {attachment?.label}
          </p>
        </div>
      );
    }, []);

    const removeAttachment = useCallback(
      (attachmentId) => {
        setMediaAttachments((prev) => {
          const target = prev?.find((item) => item?.id === attachmentId);
          if (target?.preview && target?.previewType === 'object') {
            URL.revokeObjectURL(target.preview);
          }
          const filtered =
            prev?.filter((item) => item?.id !== attachmentId) || [];
          if (attachmentId === activeAttachmentId) {
            setActiveAttachmentId(filtered?.[0]?.id || null);
          }
          return filtered;
        });
      },
      [setMediaAttachments, activeAttachmentId, setActiveAttachmentId],
    );

    const onCloseShowList = (value) => {
      setShowList(value ?? !showList);
      setSelectedMessage(null);
    };

    const reply_message_image_template =
      selectedData?.message &&
      selectedData?.message?.template?.map((item) => item);
    const previewContent =
      reply_message_image_template &&
      reply_message_image_template?.map((item) => item);

    useEffect(() => {
      clearMedia();
      setEditorState(EditorState.createEmpty());
      setNoteContent({ content: '' });
    }, [selectedPerson]);

    useEffect(() => {
      const hasUploading = attachments?.some(
        (item) => item?.status === 'uploading',
      );
      setIsUploading(Boolean(hasUploading));
      if (!attachments?.length) {
        setFileOpen(false);
        setActiveAttachmentId(null);
      } else if (
        attachments?.length > 0 &&
        !attachments?.some((item) => item?.id === activeAttachmentId)
      ) {
        setActiveAttachmentId(attachments?.[0]?.id);
      }
    }, [attachments, setIsUploading, activeAttachmentId]);

    useEffect(() => {
      if (!attachments?.length) {
        if (lockedAttachmentType) {
          setLockedAttachmentType(null);
        }
        return;
      }

      if (!lockedAttachmentType && attachments?.[0]?.type) {
        setLockedAttachmentType(attachments?.[0]?.type);
      }
    }, [attachments, lockedAttachmentType, setLockedAttachmentType]);

    // remove selected media file
    const clearMedia = () => {
      attachments.forEach((item) => {
        if (item?.preview && item?.previewType === 'object') {
          URL.revokeObjectURL(item.preview);
        }
      });
      setMsg('');
      setMediaAttachments([]);
      setSelectedData(null);
      setCaptionEmojiTargetId(null);
      setFileOpen(false);
      setLockedAttachmentType(null);
    };

    const showSendFailureToast = useCallback(() => {
      dispatch(
        updateToggleToast([
          ...toggleToast,
          {
            id: toggleToast?.length + 1,
            content: 'message send failed',
            status: 'Success',
            duration: '',
          },
        ]),
      );
    }, [dispatch, toggleToast]);

    const showAttachmentLimitToast = useCallback(() => {
      dispatch(
        updateToggleToast([
          ...toggleToast,
          {
            id: toggleToast?.length + 1,
            content: 'You can upload up to 10 media files at a time',
            status: 'Warning',
            duration: '',
          },
        ]),
      );
    }, [dispatch, toggleToast]);

    const showAttachmentTypeMismatchToast = useCallback(() => {
      dispatch(
        updateToggleToast([
          ...toggleToast,
          {
            id: toggleToast?.length + 1,
            content:
              'All attachments must match the first selected media type.',
            status: 'Warning',
            duration: '',
          },
        ]),
      );
    }, [dispatch, toggleToast]);

    // popover contents
    const triggerAttachmentPicker = useCallback(
      (type) => {
        const refMap = {
          image: fileInputRef1,
          video: fileInputRef2,
          document: fileInputRef3,
          audio: fileInputRef4,
        };
        const targetRef = refMap?.[type];
        if (targetRef?.current) {
          targetRef.current.value = null;
          targetRef.current.click();
          return true;
        }
        return false;
      },
      [fileInputRef1, fileInputRef2, fileInputRef3, fileInputRef4],
    );

    const renderAttachmentMenu = (handleClose) => (
      <div className="w-44 h-fit p-[0.5vw] flex flex-col gap-1 justify-between">
        <div className={cn('w-full max-h-12')}>
          <div
            className={cn(
              'w-full flex items-center relative gap-[0.6vw] px-[0.6vw] py-[0.5vw] rounded-[0.37vw]',
              canSendImage
                ? 'hover:bg-[var(--BG-25)] cursor-pointer'
                : 'cursor-not-allowed',
            )}
            onClick={() => {
              if (canSendImage) {
                triggerAttachmentPicker('image');
                handleClose?.();
              }
            }}
          >
            <div
              className={cn(
                'flex items-center gap-[0.6vw]',
                !canSendImage && 'opacity-50',
              )}
            >
              <ImageSvg color="var(--contentText)" width={22} height={22} />
              <p>Photos</p>
            </div>
            {!canSendImage && <UpgradeBadge iconOnly={true} />}
          </div>
        </div>
        <div className={cn('w-full max-h-12')}>
          <div
            className={cn(
              'w-full flex items-center relative gap-[0.6vw] px-[0.6vw] py-[0.5vw] rounded-[0.37vw]',
              canSendVideo
                ? 'hover:bg-[var(--BG-25)] cursor-pointer'
                : 'cursor-not-allowed',
            )}
            onClick={() => {
              if (canSendVideo) {
                triggerAttachmentPicker('video');
                handleClose?.();
              }
            }}
          >
            <div
              className={cn(
                'flex items-center gap-[0.6vw]',
                !canSendVideo && 'opacity-50',
              )}
            >
              <VideoSvg color="var(--contentText)" width={22} height={22} />
              <p>video</p>
            </div>
            {!canSendVideo && <UpgradeBadge iconOnly={true} />}
          </div>
        </div>
        <div className={cn('w-full max-h-12')}>
          <div
            className={cn(
              'w-full flex items-center relative gap-[0.6vw] px-[0.6vw] py-[0.5vw] rounded-[0.37vw]',
              canSendDocument
                ? 'hover:bg-[var(--BG-25)] cursor-pointer'
                : 'cursor-not-allowed',
            )}
            onClick={() => {
              if (canSendDocument) {
                triggerAttachmentPicker('document');
                handleClose?.();
              }
            }}
          >
            <div
              className={cn(
                'flex items-center gap-[0.6vw]',
                !canSendDocument && 'opacity-50',
              )}
            >
              <DocSvg color="var(--contentText)" width={22} height={22} />
              <p>Doc</p>
            </div>
            {!canSendDocument && <UpgradeBadge iconOnly={true} />}
          </div>
        </div>
        <div className={cn('w-full max-h-12')}>
          <div
            className={cn(
              'w-full flex items-center relative gap-[0.6vw] px-[0.6vw] py-[0.5vw] rounded-[0.37vw]',
              canSendAudio
                ? 'hover:bg-[var(--BG-25)] cursor-pointer'
                : 'cursor-not-allowed',
            )}
            onClick={() => {
              if (canSendAudio) {
                triggerAttachmentPicker('audio');
                handleClose?.();
              }
            }}
          >
            <div
              className={cn(
                'flex items-center gap-[0.6vw]',
                !canSendAudio && 'opacity-50',
              )}
            >
              <VideoSvg color="var(--contentText)" width={22} height={22} />
              <p>Audio</p>
            </div>
            {!canSendAudio && <UpgradeBadge iconOnly={true} />}
          </div>
        </div>
      </div>
    );

    const refreshConversationHistory = useCallback(async () => {
      let dynamicLimit = 100;
      const initialCount = await getConversations(
        'select',
        currentBrand?.brand_id,
        dynamicLimit,
        1,
        selectedPerson?.id,
        true,
      );

      if (initialCount !== undefined && initialCount !== 0) {
        await getConversations(
          'select',
          currentBrand?.brand_id,
          initialCount,
          1,
          selectedPerson?.id,
          true,
        );
      }

      scrollingBottom();
    }, [
      currentBrand?.brand_id,
      getConversations,
      selectedPerson?.id,
      scrollingBottom,
    ]);

    const sendSingleMessage = useCallback(
      async (messageBody) => {
        try {
          const res = await sendTextMessage({
            sendFrom: !activeTabItem?.send_from_phone_number_id
              ? selectedPhoneNumber?.send_from_phone_number_id
              : activeTabItem?.send_from_phone_number_id,
            sendTo:
              selectedPerson?.phone_country_code + selectedPerson?.to_number,
            brandId: currentBrand?.brand_id,
            msg: messageBody,
            contact_conversation_id: selectedPerson?.id,
            replay_to: selectedData?.meta_message_id,
          });
          return res?.status === 200;
        } catch (e) {
          console.warn(e);
          return false;
        }
      },
      [
        activeTabItem,
        currentBrand?.brand_id,
        selectedPhoneNumber,
        selectedPerson,
        selectedData?.meta_message_id,
      ],
    );

    const sendTemplate = async ({
      sendFrom,
      sendTo,
      metaTemplateId,
      brandId,
      subcategory_type,
      category,
      mediaUrl,
      variables,
      payment_details,
    }) => {
      setIsSending(true);
      setMembersTrue(false);

      try {
        const res = await sendTemplateMessage({
          sendFrom: sendFrom,
          sendTo: [countryCode + ' ' + sendTo],
          metaTemplateId: metaTemplateId,
          brandId: brandId,
          subcategory_type: subcategory_type,
          category: category,
          contact_conversation_id: selectedPerson?.id,
          variables: [{ ...variables, ...mediaUrl }],
          payment_details: payment_details,
        });

        if (res?.status === 200) {
          setIsSending(false);
          setMembersTrue(true);
          setOpenTemplateList(false);
          showSuccessToast(notification, 'Template sent successfully');

          let dynamicLimit = 100;
          const initialCount = await getConversations(
            'select',
            currentBrand?.brand_id,
            dynamicLimit,
            1,
            selectedPerson?.id,
            true,
          );

          if (initialCount !== undefined && initialCount !== 0) {
            await getConversations(
              'select',
              currentBrand?.brand_id,
              initialCount,
              1,
              selectedPerson?.id,
              true,
            );
          }
          scrollingBottom();
        } else {
          setIsSending(false);
          const errorMessage =
            res?.response?.data?.error ||
            res?.data?.error ||
            'Failed to send template';
          showErrorToast(notification, errorMessage);
        }
      } catch (e) {
        console.warn(e);
        setIsSending(false);
        const errorMessage =
          e?.response?.data?.error ||
          e?.data?.error ||
          'An error occurred while sending the template';
        showErrorToast(notification, errorMessage);
      }
    };

    const allowToSendMessage = useCallback(() => {
      if (hasAttachmentError) return false;
      const isEditorValid =
        editorState?.getCurrentContent()?.getPlainText()?.trim()?.length > 0;
      const hasReadyMedia = attachments?.some(
        (item) => item?.url && !item?.error,
      );
      return isEditorValid || hasReadyMedia;
    }, [attachments, editorState, hasAttachmentError]);

    const processSendQueue = useCallback(async () => {
      if (isMsgSending) return;
      const readyMedia = attachments?.filter(
        (item) => item?.url && !item?.error,
      );
      const hasText =
        editorState?.getCurrentContent()?.getPlainText()?.trim()?.length > 0;

      const payloads = [];

      readyMedia?.forEach((item) => {
        const content = {
          link: item?.url,
        };
        if (item?.type !== 'audio') {
          content.caption = item?.caption;
        }
        payloads.push({
          type: item?.type,
          [item?.type]: content,
        });
      });

      if (hasText) {
        payloads.push({
          type: 'text',
          text: {
            body: msg,
          },
        });
      }

      if (!payloads.length) return;

      setIsMsgSending(true);
      setMembersTrue(false);

      const results = await Promise.all(
        payloads.map((payload) => sendSingleMessage(payload)),
      );
      const hasFailure = results?.some((success) => !success);

      if (hasFailure) {
        showSendFailureToast();
      } else {
        setMsg('');
        setEditorState(EditorState.createEmpty());
        clearMedia();
        await refreshConversationHistory();
        if (readyMedia?.length) {
          getAttachments(attachmentFilter);
        }
      }

      setMembersTrue(true);
      setIsMsgSending(false);
    }, [
      isMsgSending,
      attachments,
      editorState,
      msg,
      setIsMsgSending,
      setMembersTrue,
      sendSingleMessage,
      showSendFailureToast,
      clearMedia,
      refreshConversationHistory,
      getAttachments,
      attachmentFilter,
    ]);

    const handleSendMessage = useCallback(() => {
      if (!allowToSendMessage()) return;
      processSendQueue();
    }, [allowToSendMessage, processSendQueue]);

    // quick replies
    const fetchQuickReplies = async () => {
      setQuickRepliesLoading(true);
      const payLoad = {
        brand_id: currentBrand?.brand_id,
      };
      const response = await inboxQuickReplies(payLoad);
      if (response && response?.data?.quick_reply_groups?.length >= 0) {
        setQuickReplies(response?.data?.quick_reply_groups);
        setQuickRepliesLoading(false);
      }
    };

    const handleSelectedMessage = (message) => {
      const contentState = editorState.getCurrentContent();
      const selection = editorState.getSelection();
      const contentStateWithText = Modifier.insertText(
        contentState,
        selection,
        message,
      );
      const newEditorState = EditorState.push(
        editorState,
        contentStateWithText,
        'insert-characters',
      );
      setEditorState(newEditorState);
      setShowQuickRepliesManager(false);
      onEditorChange(newEditorState);
    };

    // template
    const handleCancel = () => {
      setOpenTemplateList(false);
    };

    // Function to strip only actual link entities
    function removeLinksFromRawContent(rawContent) {
      const { entityMap, blocks } = rawContent;

      // Remove all LINK entities from entityMap
      const linkEntityKeys = Object.entries(entityMap)
        .filter(([_, entity]) => entity.type === 'LINK')
        .map(([key]) => key);

      // Remove links from inlineStyleRanges and entityRanges
      const newBlocks = blocks.map((block) => {
        if (!block.entityRanges) return block;

        const newEntityRanges = block.entityRanges.filter(
          (range) => !linkEntityKeys.includes(range.key.toString()),
        );

        return {
          ...block,
          entityRanges: newEntityRanges,
        };
      });

      return {
        blocks: newBlocks,
        entityMap: {}, // Clear all entities
      };
    }

    // Debounced typing indicator
    const debouncedTyping = useMemo(
      () =>
        debounce(() => {
          try {
            const payLoad = {
              brand_id: currentBrand?.brand_id,
              contact_conversation_id: selectedPerson?.id,
              is_typing: true,
            };
            markMessagesAsRead(payLoad);
          } catch (error) {
            console.error('Error sending typing status:', error);
          }
        }, 1000), // Wait for 1 second of inactivity
      [currentBrand, selectedPerson],
    );

    // Cleanup debounce on unmount
    useEffect(() => {
      return () => {
        debouncedTyping.cancel();
      };
    }, [debouncedTyping]);

    // editor
    const onEditorChange = (newState) => {
      setEditorState(newState);
      setShowEmojiPicker(false);
      setShowEmojiPickerCaption(false);

      const content = newState?.getCurrentContent();
      const rawObject = convertToRaw(content);

      if (content.hasText()) {
        debouncedTyping();
      }

      const rawWithoutLinks = removeLinksFromRawContent(rawObject);
      const markdownString = draftToMarkdown(rawWithoutLinks);
      setMsg(markdownString);
    };

    const handleCaptionChange = (attachmentId, value) => {
      updateAttachment(attachmentId, { caption: value });
    };

    const handleBeforeInput = (val) => {
      const contentState = editorState.getCurrentContent();
      const selection = editorState.getSelection();
      const blockKey = selection.getStartKey();
      const block = contentState.getBlockForKey(blockKey);
      const blockText = block.getText();
      const anchorOffset = selection.getAnchorOffset();

      const MAX_CHAR_LIMIT = 1024;
      const textLength = contentState.getPlainText().length;

      // Block further input if character limit is reached
      if (val && textLength >= MAX_CHAR_LIMIT) {
        return 'handled';
      }

      // Only act when user types a space
      if (val !== ' ') {
        return 'not-handled';
      }

      // Simulate what the text would be if this space were added
      const textBeforeInput = blockText.slice(0, anchorOffset);
      const simulatedText = textBeforeInput + val;

      // Match patterns at the beginning of the line
      const listTypeMap = {
        '- ': 'unordered-list-item',
        '* ': 'unordered-list-item',
        '1. ': 'ordered-list-item',
      };

      const matchedEntry = Object.entries(listTypeMap).find(([pattern]) =>
        simulatedText.startsWith(pattern),
      );

      if (!matchedEntry) {
        return 'not-handled';
      }

      const [triggerPattern, listType] = matchedEntry;

      // Toggle the block type (UL or OL)
      let newEditorState = RichUtils.toggleBlockType(editorState, listType);
      let newContent = newEditorState.getCurrentContent();
      let newBlock = newContent.getBlockForKey(blockKey);

      // If successfully changed, remove the trigger text
      if (newBlock.getType() === listType) {
        const newText = blockText
          .replace(triggerPattern.trim(), '')
          .trimStart();

        const updatedBlock = newBlock.set('text', newText);
        const updatedContent = newContent.set(
          'blockMap',
          newContent.getBlockMap().set(blockKey, updatedBlock),
        );

        const finalEditorState = EditorState.push(
          newEditorState,
          updatedContent,
          'change-block-type',
        );

        setEditorState(finalEditorState);
        return 'handled';
      }

      return 'not-handled';
    };

    const onClose = () => {
      setFileOpen(false);
      setMediaAttachments([]);
      setCaptionEmojiTargetId(null);
      setLockedAttachmentType(null);
      // Reset any other related state if needed
      // setMediaCaption("");
      // setSelectedFile(null);
      // setSelectedFileType(null);
      // setSelectedFileURL(null);
      // setIsClearingMedia(true);
    };

    const handleEmojiClickCaption = (emoji) => {
      if (!captionEmojiTargetId) return;
      updateAttachment(captionEmojiTargetId, (item) => ({
        caption: `${item?.caption || ''}${emoji.native}`,
      }));
      setCaptionEmojiTargetId(null);
    };

    const handleSelectAttachment = useCallback((attachmentId) => {
      setActiveAttachmentId(attachmentId);
    }, []);

    const handleAddAttachmentClick = useCallback(() => {
      const sessionType = lockedAttachmentType || attachments?.[0]?.type;
      if (!sessionType) return;
      triggerAttachmentPicker(sessionType);
    }, [lockedAttachmentType, attachments, triggerAttachmentPicker]);

    const mapKeyToEditorCommand = (e) => {
      if (e.key === 'b' && e.metaKey) return 'bold';

      if (e.key === 'i' && e.metaKey) return 'italic';

      if (e.key === 's' && e.metaKey) return 'strikethrough';

      if (e.key === 'Enter' && !e.shiftKey) {
        return 'send-message';
      }

      return getDefaultKeyBinding(e);
    };

    const handleKeyCommand = (command, editorState, containerRef) => {
      const newState = RichUtils.handleKeyCommand(editorState, command);

      if (newState) {
        setEditorState(newState);
        return 'handled';
      }

      if (command === 'send-message') {
        if (!isMsgSending && allowToSendMessage()) {
          processSendQueue();
          return 'handled';
        }
      }
      return 'not-handled';
    };

    const handleToolbarClick = (style) => {
      const blockStyles = ['unordered-list-item', 'ordered-list-item']; // Add more if needed
      const isBlock = blockStyles.includes(style);

      const newState = isBlock
        ? RichUtils.toggleBlockType(editorState, style)
        : RichUtils.toggleInlineStyle(editorState, style);

      setEditorState(newState);
    };

    const handleFilePaste = useCallback(
      (text, fileType) => {
        if (!fileType) return 'handled';
        let added = false;
        const normalizedType = fileType?.toLowerCase();

        if (
          lockedAttachmentType &&
          normalizedType &&
          lockedAttachmentType !== normalizedType
        ) {
          showAttachmentTypeMismatchToast();
          return 'handled';
        }

        const newAttachment = {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          type: normalizedType,
          label: fileType?.toUpperCase(),
          file: null,
          preview: text,
          previewType: 'remote',
          url: text,
          caption: '',
          status: 'ready',
          error: '',
        };
        setMediaAttachments((prev) => {
          if ((prev?.length || 0) >= MAX_ATTACHMENTS) {
            showAttachmentLimitToast();
            return prev;
          }
          added = true;
          return [...(prev || []), newAttachment];
        });
        if (added) {
          setLockedAttachmentType((prev) => prev || normalizedType);
          setFileOpen(true);
        }
        return 'handled';
      },
      [
        setMediaAttachments,
        showAttachmentLimitToast,
        setFileOpen,
        lockedAttachmentType,
        showAttachmentTypeMismatchToast,
        setLockedAttachmentType,
      ],
    );

    const handlePastedText = (text) => {
      const textLength = editorState.getCurrentContent().getPlainText().length;

      if (text && textLength >= 1024) {
        return 'handled';
      }

      if (text) {
        if (/\.(jpeg|jpg|gif|png)$/.test(text)) {
          return handleFilePaste(text, 'image');
        }

        if (/\.(mp4|webm)$/.test(text)) {
          return handleFilePaste(text, 'video');
        }

        if (/\.(pdf|doc|docx|ppt|pptx|xls|xlsx|txt)$/.test(text)) {
          return handleFilePaste(text, 'document');
        }

        if (/\.(aac|amr|mp3|m4a|ogg)$/.test(text)) {
          return handleFilePaste(text, 'audio');
        }
      }

      return 'not-handled';
    };

    // media files
    // handle upload media files

    const handleUploadFiles = async (e, format) => {
      const files = Array.from(e?.target?.files || []);
      if (!files.length) return;
      const normalizedFormat = format?.toLowerCase();

      if (
        lockedAttachmentType &&
        normalizedFormat &&
        lockedAttachmentType !== normalizedFormat
      ) {
        showAttachmentTypeMismatchToast();
        if (e?.target) e.target.value = null;
        return;
      }

      if (attachments.length >= MAX_ATTACHMENTS) {
        showAttachmentLimitToast();
        if (e?.target) e.target.value = null;
        return;
      }

      const availableSlots = MAX_ATTACHMENTS - attachments.length;
      const filesToUpload = files.slice(0, availableSlots);
      if (!filesToUpload.length) {
        showAttachmentLimitToast();
        if (e?.target) e.target.value = null;
        return;
      }

      if (filesToUpload.length < files.length) {
        showAttachmentLimitToast();
      }

      if (normalizedFormat) {
        setLockedAttachmentType((prev) => prev || normalizedFormat);
      }

      setFileOpen(true);

      const createdAttachments = filesToUpload.map((file) => {
        const previewSource =
          normalizedFormat === 'document' ? null : URL.createObjectURL(file);
        const limit = sizeLimitByType[normalizedFormat] || Infinity;
        const hasSizeError = file?.size > limit;

        return {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          type: normalizedFormat,
          label: normalizedFormat?.toUpperCase(),
          file,
          preview: previewSource,
          previewType: previewSource ? 'object' : null,
          url: '',
          caption: '',
          status: hasSizeError ? 'error' : 'uploading',
          error: hasSizeError ? sizeErrorMessage[normalizedFormat] : '',
        };
      });

      setMediaAttachments((prev) => [...(prev || []), ...createdAttachments]);

      await Promise.allSettled(
        createdAttachments
          .filter((item) => item?.status === 'uploading')
          .map(async (item) => {
            try {
              const responseFile = await uploadFile(item.file, {
                path: 'inbox',
                brand_id: currentBrand?.brand_id,
                phone_number:
                  selectedPerson?.phone_country_code +
                  selectedPerson?.to_number,
              });
              if (responseFile) {
                updateAttachment(item.id, {
                  url: responseFile,
                  status: 'ready',
                });
              } else {
                updateAttachment(item.id, {
                  status: 'error',
                  error: 'Unable to upload file. Try again.',
                });
              }
            } catch (error) {
              console.error('Error uploading attachment:', error);
              updateAttachment(item.id, {
                status: 'error',
                error: 'Unable to upload file. Try again.',
              });
            }
          }),
      );

      if (e?.target) {
        e.target.value = null;
      }
    };

    // notes
    const noteCreate = () => {
      setIsNoteLoading(true);

      const payLoad = {
        brand_id: currentBrand?.brand_id,
        contact_conversation_id: selectedPerson?.id,
        content: noteContent.content,
      };

      const showPayLoad = {
        brand_id: currentBrand?.brand_id,
        contact_conversation_id: selectedPerson?.id,
        limit: 100,
        page: 1,
      };

      let dynamicLimit = 100;

      createNote(payLoad)
        .then((res) => {
          if (res?.status === 200) {
            // showSuccessToast(notification, "Note created");
            setNoteContent(({ id, ...rest }) => rest);
            setNoteContent({ content: '' });
            getNotes(currentBrand?.brand_id, selectedPerson?.id);

            getConversations(
              'select',
              currentBrand?.brand_id,
              dynamicLimit,
              page,
              selectedPerson?.id,
              true,
            );
          } else {
            showErrorToast(
              notification,
              'Failed to create note: ' + res?.response?.data?.error,
            );
            setIsNoteLoading(false); // Also handle failure
          }

          // ✅ Move it here
          setIsNoteLoading(false);
        })
        .catch((error) => {
          console.error('Failed to create note:', error);
          setIsNoteLoading(false); // Also handle failure
        });
    };

    const noteUpdate = () => {
      setIsNoteLoading(true);

      const payLoad = {
        brand_id: currentBrand?.brand_id,
        contact_conversation_id: selectedPerson?.id,
        content: noteContent.content,
        tag_id: noteContent.id,
      };

      // const showPayLoad = {
      //   brand_id: currentBrand?.brand_id,
      //   contact_conversation_id: selectedPerson?.id,
      //   limit: 100,
      //   page: 1,
      // };

      let dynamicLimit = 100;

      updateNote(payLoad)
        .then((res) => {
          if (res?.status === 200) {
            setNoteContent(({ id, ...rest }) => rest);
            setNoteContent({ content: '' });

            getNotes(currentBrand?.brand_id, selectedPerson?.id);
            getConversations(
              'select',
              currentBrand?.brand_id,
              dynamicLimit,
              page,
              selectedPerson?.id,
              true,
            );
          }
        })
        .catch((err) => {
          console.error('Error in updateNote:', err);
        })
        .finally(() => {
          setIsNoteLoading(false); // ✅ always runs
        });
    };

    const handleNoteEmojiClick = (emoji) => {
      setTimeout(() => {
        const textarea =
          textareaRef.current || document.querySelector('textarea');

        if (!textarea) {
          console.warn('Textarea not found');
          return;
        }

        const emojiChar = emoji.native || emoji.emoji || emoji.colons || '';
        const { selectionStart, selectionEnd } = textarea;
        const value = noteContent.content;

        const newText = value
          ? value?.slice(0, selectionStart) +
            emojiChar +
            value?.slice(selectionEnd)
          : emojiChar;

        setNoteContent((prev) => ({
          ...prev,
          content: newText,
        }));

        const newPos = selectionStart + emojiChar.length;
        textarea.focus();
        textarea.setSelectionRange(newPos, newPos);
        setShowEmojiPickerCaption(false);
      }, 0);
    };

    // emoji
    const handleEmojiClick = (emoji) => {
      const contentState = editorState?.getCurrentContent();
      const selection = editorState?.getSelection();

      if (selection.isCollapsed()) {
        const contentStateWithEmoji = contentState.createEntity(
          'emoji',
          'IMMUTABLE',
          {
            emoji: emoji.native,
          },
        );
        const entityKey = contentStateWithEmoji?.getLastCreatedEntityKey();

        const contentStateWithEntity = Modifier.insertText(
          contentStateWithEmoji,
          selection,
          emoji.native,
          null,
          entityKey,
        );

        const newEditorStateWithEntity = EditorState.push(
          editorState,
          contentStateWithEntity,
          'insert-fragment',
        );

        setEditorState(newEditorStateWithEntity);
        // setBodyObj({ ...bodyObj, text: newEditorStateWithEntity });
        setShowEmojiPicker(false);
        onEditorChange(newEditorStateWithEntity);
      }
    };

    const reply_message_template =
      selectedData?.message &&
      selectedData?.message?.template?.map((item) => item?.text);

    return (
      <>
        <div
          className={`w-full h-auto flex px-[1vw] pt-[1vw] pb-[5px] mx-[12px]}`}
        >
          {conversationType?.includes('closed') ? (
            chatToggle ? (
              <div className="w-full px-[1vw] py-[1vh] bg-[var(--BG-25)] text-[var(--white)] flex flex-col  rounded-[0.5vw]">
                <div className="w-full mx-auto  mt-10">
                  <p className="text-[1rem] text-[#4C515B] font-[500]">
                    Your open conversation has timed out.
                  </p>
                  <p className="text-[0.875rem] font-[300] text-[#8796AF]">
                    start a conversation using templates by clicking the button
                    below.
                  </p>
                </div>

                <div className="flex items-center gap-[0.5vw]">
                  <div
                    className="w-auto flex-row align-center justify-start p-5 pointer"
                    onClick={() => {
                      setOpenTemplateList(true);
                    }}
                  >
                    <span className="inbox-button px-2.5 py-1.5 b-radius-6">
                      Choose Template
                    </span>
                  </div>
                  <div className="w-auto flex items-center gap-[0.5vw] border-[#ececeb] pr-[1vw]">
                    <button
                      className={` flex items-center gap-[0.3vw] border text-[rgba(135, 150, 175, 1)] var(--font-600) rounded-[0.3vw] p-1 hover:bg-[#ececeb] transition-colors duration-200 ${
                        canSendNotes ? 'cursor-pointer' : 'cursor-not-allowed'
                      }`}
                      onClick={() => {
                        if (canSendNotes) {
                          handleChat();
                        }
                      }}
                    >
                      <div
                        className={cn(
                          'flex items-center gap-[0.3vw]',
                          !canSendNotes && 'opacity-50',
                        )}
                      >
                        <InboxNoteIcon fill="#000000" />
                        <span className="text-gray-700 font-medium text-[14px]">
                          Add a Note
                        </span>
                      </div>
                      {!canSendNotes && <UpgradeBadge />}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <NoteEditor
                noteContent={noteContent}
                setNoteContent={setNoteContent}
                handleChat={handleChat}
                showEmojiPickerCaption={showEmojiPickerCaption}
                setShowEmojiPickerCaption={setShowEmojiPickerCaption}
                data={data}
                assignGetData={assignGetData}
                textareaRef={textareaRef}
                handleNoteEmojiClick={handleNoteEmojiClick}
                noteUpdate={noteUpdate}
                noteCreate={noteCreate}
                isNoteLoading={isNoteLoading}
              />
            )
          ) : (
            <>
              {isChatResolved ? (
                <div
                  style={{ width: '100%' }}
                  className={`textEditorContainer flex-column space-between p-10 bg-[white] mt-10 ${
                    attachments?.length || isUploading
                      ? '!h-[15vh]'
                      : '!h-[13vh]'
                  }`}
                >
                  <div className="flex-column">
                    <span className="ml-5 text-[1.1rem] text-[#4C515B] font-[500]">
                      That chat is resolved
                    </span>
                    <span className="ml-5 text-[0.875rem] text-[#8796AF] font-[300]">
                      You can reopen the chat to follow up with the customer.
                    </span>
                  </div>
                  <div
                    className="w-auto flex-row align-center justify-start p-5 mb-5 pointer"
                    onClick={() => {
                      onResolvedClick(item);
                    }}
                  >
                    <span className="inbox-button px-2.5 py-1.5 b-radius-6">
                      Reopen Chat
                    </span>
                  </div>
                </div>
              ) : (
                <>
                  {chatToggle ? (
                    <div
                      className={`flex flex-col textEditorContainer overflow-hidden bg-white p-[10px] gap-y-[18px]`}
                      style={{
                        width: '100%',
                        minHeight: '15vh',
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      {selectedData && (
                        <div className="b-radius-6 relative w-full pt-2 px-2">
                          <div className="w-full flex-row align-center bg-[var(--channelButton)] inbox12px-400 text-[var(--font-600)] b-radius-6 mb-10">
                            <div className="h-[6vh] bg-[#6564CA] w-1" />
                            <div className="flex flex-1 items-center justify-between ml-10 py-1.5">
                              {Object.values(MESSAGE_TYPES).includes(
                                selectedData?.message?.type,
                              ) && (
                                <>
                                  {mediaIcons[selectedData?.message?.type] && (
                                    <img
                                      src={
                                        mediaIcons[selectedData?.message?.type]
                                      }
                                      alt={selectedData?.message?.type}
                                      className="media-icon flex-shrink-0"
                                    />
                                  )}
                                  <div className="w-full flex justify-between items-center min-w-0">
                                    {(() => {
                                      const messageType =
                                        selectedData?.message?.type;

                                      switch (messageType) {
                                        case 'image':
                                          return (
                                            <>
                                              <p className="truncate">
                                                {selectedData?.message?.image
                                                  ?.caption || 'Image'}
                                              </p>
                                              <img
                                                src={
                                                  selectedData?.message?.image
                                                    ?.link
                                                }
                                                className="w-[40px] h-[40px] object-cover rounded-lg ml-10 mr-10 flex-shrink-0"
                                                alt="Preview"
                                              />
                                            </>
                                          );

                                        case 'video':
                                          return (
                                            <>
                                              <p className="truncate">
                                                {selectedData?.message?.video
                                                  ?.caption || 'Video'}
                                              </p>
                                              <video className="w-[40px] h-[40px] object-cover rounded-lg ml-10 mr-10 flex-shrink-0">
                                                <source
                                                  src={
                                                    selectedData?.message?.video
                                                      ?.link
                                                  }
                                                  type="video/mp4"
                                                />
                                              </video>
                                            </>
                                          );

                                        case 'document':
                                          return (
                                            <>
                                              <p className="truncate">
                                                {selectedData?.message?.document
                                                  ?.caption || 'Document'}
                                              </p>
                                              <iframe
                                                className="w-[40px] h-[40px] object-cover rounded-lg ml-10 mr-10 flex-shrink-0"
                                                src={
                                                  selectedData?.message
                                                    ?.document?.link
                                                }
                                                title="Document preview"
                                              />
                                            </>
                                          );

                                        case 'audio':
                                          return (
                                            <>
                                              <p className="truncate">
                                                {selectedData?.message?.audio
                                                  ?.caption || 'Audio'}
                                              </p>
                                              <audio className="w-[40px] h-[40px] object-cover rounded-lg ml-10 mr-10 flex-shrink-0">
                                                <source
                                                  src={
                                                    selectedData?.message?.audio
                                                      ?.link
                                                  }
                                                  type="audio/mp3"
                                                />
                                              </audio>
                                            </>
                                          );

                                        case 'text':
                                          return (
                                            <div className="w-full break-words new-body-text rounded-[0.6vw] rounded-tl-none">
                                              <div
                                                className="overflow-hidden text-ellipsis"
                                                style={{
                                                  display: '-webkit-box',
                                                  WebkitLineClamp: 2,
                                                  WebkitBoxOrient: 'vertical',
                                                  overflow: 'hidden',
                                                }}
                                                dangerouslySetInnerHTML={{
                                                  __html: replaceFormating(
                                                    selectedData?.message?.text
                                                      ?.body,
                                                  ),
                                                }}
                                              />
                                            </div>
                                          );

                                        case 'button':
                                          return (
                                            <div
                                              className="new-body-text rounded-[0.6vw] rounded-tl-none overflow-hidden text-ellipsis w-full"
                                              style={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                              }}
                                            >
                                              {
                                                selectedData?.message?.button
                                                  ?.text
                                              }
                                            </div>
                                          );

                                        case 'template':
                                          return (
                                            <>
                                              <div
                                                className="max-w-[60ch] ml-10 overflow-hidden text-ellipsis"
                                                style={{
                                                  display: '-webkit-box',
                                                  WebkitLineClamp: 2,
                                                  WebkitBoxOrient: 'vertical',
                                                  overflow: 'hidden',
                                                }}
                                              >
                                                {reply_message_template}
                                              </div>
                                              <div className="flex-shrink-0">
                                                {previewContent?.length > 0 && (
                                                  <>
                                                    {previewContent[0]
                                                      ?.format === 'IMAGE' && (
                                                      <img
                                                        src={
                                                          previewContent[0]
                                                            ?.example
                                                            ?.header_handle?.[0]
                                                        }
                                                        className="w-[40px] h-[40px] object-cover rounded-lg ml-10 mr-10"
                                                        alt="Template preview"
                                                      />
                                                    )}
                                                    {previewContent[0]
                                                      ?.format === 'VIDEO' && (
                                                      <video className="w-[40px] h-[40px] object-cover rounded-lg ml-10 mr-10">
                                                        <source
                                                          src={
                                                            previewContent[0]
                                                              ?.example
                                                              ?.header_handle?.[0]
                                                          }
                                                          type="video/mp4"
                                                        />
                                                      </video>
                                                    )}
                                                    {previewContent[0]
                                                      ?.format ===
                                                      'DOCUMENT' && (
                                                      <iframe
                                                        className="w-[40px] h-[40px] object-cover rounded-lg ml-10 mr-10"
                                                        src={
                                                          previewContent[0]
                                                            ?.example
                                                            ?.header_handle?.[0]
                                                        }
                                                        title="Template document preview"
                                                      />
                                                    )}
                                                    {previewContent[0]
                                                      ?.format === 'AUDIO' && (
                                                      <audio className="w-[40px] h-[40px] object-cover rounded-lg ml-10 mr-10">
                                                        <source
                                                          src={
                                                            previewContent[0]
                                                              ?.example
                                                              ?.header_handle?.[0]
                                                          }
                                                          type="audio/mp3"
                                                        />
                                                      </audio>
                                                    )}
                                                  </>
                                                )}
                                              </div>
                                            </>
                                          );

                                        case 'interactive':
                                          return (
                                            <div className="w-full break-words new-body-text rounded-[0.6vw] rounded-tl-none">
                                              <div
                                                className="overflow-hidden text-ellipsis"
                                                style={{
                                                  display: '-webkit-box',
                                                  WebkitLineClamp: 2,
                                                  WebkitBoxOrient: 'vertical',
                                                  overflow: 'hidden',
                                                }}
                                                dangerouslySetInnerHTML={{
                                                  __html: replaceFormating(
                                                    selectedData?.message
                                                      ?.interactive?.body?.text,
                                                  ),
                                                }}
                                              />
                                            </div>
                                          );

                                        case 'contacts':
                                          return (
                                            <div className="w-full break-words new-body-text rounded-[0.6vw] rounded-tl-none">
                                              <div
                                                className="overflow-hidden text-ellipsis"
                                                style={{
                                                  display: '-webkit-box',
                                                  WebkitLineClamp: 2,
                                                  WebkitBoxOrient: 'vertical',
                                                  overflow: 'hidden',
                                                }}
                                                dangerouslySetInnerHTML={{
                                                  __html: replaceFormating(
                                                    selectedData?.message
                                                      ?.contacts?.[0]?.name
                                                      ?.formatted_name,
                                                  ),
                                                }}
                                              />
                                            </div>
                                          );

                                        case 'location':
                                          return (
                                            <div className="w-full break-words new-body-text rounded-[0.6vw] rounded-tl-none">
                                              <div
                                                className="overflow-hidden text-ellipsis"
                                                style={{
                                                  display: '-webkit-box',
                                                  WebkitLineClamp: 2,
                                                  WebkitBoxOrient: 'vertical',
                                                  overflow: 'hidden',
                                                }}
                                                dangerouslySetInnerHTML={{
                                                  __html: replaceFormating(
                                                    selectedData?.message
                                                      ?.location?.name,
                                                  ),
                                                }}
                                              />
                                            </div>
                                          );
                                        default:
                                          return null;
                                      }
                                    })()}
                                  </div>
                                  <img
                                    src={ICONS?.popupX}
                                    alt="close"
                                    className="w-[1.8vw] h-[1.8vw] pointer flex-shrink-0"
                                    onClick={() => setSelectedData(null)}
                                  />
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      <div
                        // ref={editorRef}
                        className={`w-full text-[0.95rem] max-h-[10vh] text-[var(--textBlack)] overflow-y-scroll listScroll break-words whitespace-pre-wrap`}
                        onDoubleClick={() => setShowToolbar(!showToolbar)}
                      >
                        <Editor
                          className=""
                          ref={editorRef}
                          editorState={editorState}
                          handleKeyCommand={(command, state) =>
                            handleKeyCommand(command, state, editorRef)
                          }
                          keyBindingFn={mapKeyToEditorCommand}
                          onChange={onEditorChange}
                          placeholder={
                            editorState.getCurrentContent().hasText() ||
                            editorState
                              .getCurrentContent()
                              .getBlockMap()
                              .first()
                              .getType() !== 'unstyled'
                              ? ''
                              : 'Write your message or type / to pick a quick Reply messages'
                          }
                          handleBeforeInput={handleBeforeInput}
                          handlePastedText={handlePastedText}
                          editorClassName="break-words whitespace-pre-wrap"
                          readOnly={
                            isMsgSending ||
                            (ccChatbotStatus && item?.agent_engagement_status)
                          }
                        />
                      </div>
                      {attachments?.length > 0 && (
                        <div className="w-full px-[1vw]">
                          {isUploading && (
                            <div className="w-full h-[6.5vh] flex items-center">
                              <Loader Width={30} Height={30} loaderBg="white" />
                            </div>
                          )}
                          <Drawer
                            rootClassName="message-input-drawer"
                            title="Send Media"
                            placement="bottom"
                            open={fileOpen}
                            onClose={onClose}
                            onOpenChange={(open) => {
                              if (!open) {
                                onClose();
                              } else {
                                setFileOpen(true);
                              }
                            }}
                            destroyOnClose={true}
                          >
                            <div className="w-full bg-white rounded-2xl space-y-2">
                              <div className="flex items-center justify-between flex-wrap gap-3">
                                <div>
                                  <p className="text-[1rem] font-semibold text-[var(--textBlack)]">
                                    Review media
                                  </p>
                                  {activeAttachment && (
                                    <p className="text-[0.75rem] text-[var(--font-400)] max-w-[20vw] truncate">
                                      {activeAttachment?.file?.name ||
                                        `media.${activeAttachment?.label?.toLowerCase()}`}
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-[0.75rem] text-[var(--font-400)]">
                                    {attachments?.length} / {MAX_ATTACHMENTS}
                                  </span>
                                </div>
                              </div>

                              <div className="relative flex flex-col items-center justify-center gap-4 py-10 bg-[#F6F7FA] rounded-[1vw] min-h-[30vh]">
                                {activeAttachment && (
                                  <button
                                    type="button"
                                    className="absolute top-4 right-4 bg-white rounded-full shadow p-[0.3vw] flex items-center justify-center"
                                    onClick={() =>
                                      removeAttachment(activeAttachment?.id)
                                    }
                                  >
                                    <Delete
                                      width="0.9vw"
                                      height="0.9vw"
                                      color="var(--fontRed)"
                                    />
                                  </button>
                                )}
                                {activeAttachment ? (
                                  <>
                                    {renderPrimaryPreview(activeAttachment)}
                                    {activeAttachment?.status ===
                                      'uploading' && (
                                      <p className="text-[var(--primary)] text-[0.85rem]">
                                        Uploading…
                                      </p>
                                    )}
                                    {activeAttachment?.error && (
                                      <p className="text-[var(--fontRed)] text-[0.85rem]">
                                        {activeAttachment?.error}
                                      </p>
                                    )}
                                  </>
                                ) : (
                                  <p className="text-[var(--font-400)] text-[0.9rem]">
                                    Select a media to preview
                                  </p>
                                )}
                              </div>

                              {activeAttachment &&
                                activeAttachment?.type !== 'audio' && (
                                  <div className="flex items-center gap-2 bg-[#F8F9FC] rounded-full px-4 py-2">
                                    <input
                                      className="flex-1 bg-transparent text-[0.9rem] focus:outline-none"
                                      autoComplete="off"
                                      maxLength={256}
                                      type="text"
                                      placeholder="Add a caption"
                                      value={activeAttachment?.caption || ''}
                                      onChange={(e) =>
                                        handleCaptionChange(
                                          activeAttachment?.id,
                                          e.target.value,
                                        )
                                      }
                                      disabled={isMsgSending}
                                    />
                                    <Popover
                                      trigger={['click']}
                                      open={
                                        captionEmojiTargetId ===
                                        activeAttachment?.id
                                      }
                                      onOpenChange={(open) =>
                                        setCaptionEmojiTargetId(
                                          open ? activeAttachment?.id : null,
                                        )
                                      }
                                      arrow={false}
                                      content={
                                        <Picker
                                          data={data}
                                          onEmojiSelect={
                                            handleEmojiClickCaption
                                          }
                                          searchPosition="none"
                                          previewPosition="none"
                                          width={`5vw`}
                                        />
                                      }
                                    >
                                      <button
                                        className="size-[1.5vw] flex items-center justify-center rounded-full hover:bg-slate-100"
                                        type="button"
                                        onClick={() =>
                                          setCaptionEmojiTargetId(
                                            captionEmojiTargetId ===
                                              activeAttachment?.id
                                              ? null
                                              : activeAttachment?.id,
                                          )
                                        }
                                      >
                                        <img
                                          src={ICONS?.step3EmojiIcon}
                                          alt="emoji"
                                          className="size-[1vw]"
                                        />
                                      </button>
                                    </Popover>
                                  </div>
                                )}

                              <div className="flex items-center gap-3 flex-wrap">
                                {attachments?.map((attachment) => {
                                  const previewSource =
                                    attachment?.preview || attachment?.url;
                                  const isImage = attachment?.type === 'image';
                                  const isVideo = attachment?.type === 'video';
                                  const showPreview =
                                    (isImage || isVideo) && previewSource;

                                  return (
                                    <div
                                      key={attachment?.id}
                                      className={cn(
                                        'group relative w-[4.5vw] h-[4.5vw] rounded-lg overflow-hidden border-2 flex-shrink-0 flex items-center justify-center',
                                        attachment?.error
                                          ? 'border-[var(--fontRed)]'
                                          : activeAttachmentId ===
                                            attachment?.id
                                          ? 'border-[var(--new-primary)]'
                                          : 'border-[var(--border-input)]',
                                      )}
                                    >
                                      <button
                                        type="button"
                                        onClick={() =>
                                          removeAttachment(attachment?.id)
                                        }
                                        className="absolute top-[3px] right-[2px] size-4 rounded-full bg-white shadow-sm flex items-center justify-center text-[0.65rem] text-[var(--fontRed)] opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:scale-105"
                                        aria-label="Remove attachment"
                                      >
                                        ×
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleSelectAttachment(attachment?.id)
                                        }
                                        className="w-full h-full flex items-center justify-center"
                                      >
                                        {showPreview ? (
                                          isImage ? (
                                            <img
                                              src={previewSource}
                                              alt=""
                                              className="w-full h-full object-cover"
                                            />
                                          ) : isVideo ? (
                                            <div className="relative w-full h-full flex items-center justify-center bg-black/5">
                                              <video
                                                src={previewSource}
                                                className="w-full h-full object-cover"
                                              />
                                              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                                                <div className="w-[1.5vw] h-[1.5vw] bg-white/80 rounded-full flex items-center justify-center">
                                                  <svg
                                                    width="12"
                                                    height="14"
                                                    viewBox="0 0 12 14"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                  >
                                                    <path
                                                      d="M11 6.26795C11.3333 6.46791 11.3333 6.93209 11 7.13205L2 12.7942C1.66667 12.9942 1.25 12.762 1.25 12.362V1.13797C1.25 0.737973 1.66667 0.50584 2 0.705803L11 6.26795Z"
                                                      fill="currentColor"
                                                    />
                                                  </svg>
                                                </div>
                                              </div>
                                            </div>
                                          ) : null
                                        ) : (
                                          <div className="flex flex-col items-center justify-center p-2">
                                            {mediaIcons?.[
                                              attachment?.label
                                            ] && (
                                              <img
                                                src={
                                                  mediaIcons?.[
                                                    attachment?.label
                                                  ]
                                                }
                                                alt=""
                                                className="w-[1.2vw] h-[1.2vw] mb-1"
                                              />
                                            )}
                                            <span className="text-[0.6vw] font-medium text-[var(--font-600)]">
                                              {attachment?.label}
                                            </span>
                                          </div>
                                        )}
                                      </button>
                                      {attachment?.error && (
                                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-[var(--fontRed)] rounded-full flex items-center justify-center">
                                          <svg
                                            width="8"
                                            height="8"
                                            viewBox="0 0 8 8"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <path
                                              d="M4 2.66699V4.66699M4 5.33366V5.66699M1.33333 8H6.66667C7.40305 8 8 7.40305 8 6.66667V1.33333C8 0.596954 7.40305 0 6.66667 0H1.33333C0.596954 0 0 0.596954 0 1.33333V6.66667C0 7.40305 0.596954 8 1.33333 8Z"
                                              fill="white"
                                            />
                                            <path
                                              d="M4 2.66699V4.66699M4 5.33366V5.66699M1.33333 8H6.66667C7.40305 8 8 7.40305 8 6.66667V1.33333C8 0.596954 7.40305 0 6.66667 0H1.33333C0.596954 0 0 0.596954 0 1.33333V6.66667C0 7.40305 0.596954 8 1.33333 8Z"
                                              stroke="white"
                                            />
                                            <path
                                              d="M4 4.5V5.5M4 4.5V4.5M4 5.5V5.5M4 4.5V3"
                                              stroke="white"
                                              stroke-linecap="round"
                                            />
                                          </svg>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                                {attachments?.length < MAX_ATTACHMENTS && (
                                  <button
                                    type="button"
                                    className="w-[2.4vw] h-[2.4vw] rounded-full border border-dashed border-[var(--border-input)] flex items-center justify-center text-[1.2rem] text-[var(--font-600)]"
                                    onClick={handleAddAttachmentClick}
                                  >
                                    +
                                  </button>
                                )}
                              </div>
                              {hasAttachmentError && (
                                <p className="text-[var(--fontRed)] text-[0.75rem]">
                                  Remove or fix attachments that exceed limits
                                  to continue.
                                </p>
                              )}

                              <div className="flex items-center justify-end pt-2">
                                <button
                                  className={`flex items-center gap-2 rounded-[0.37vw] px-4 py-2 text-white ${
                                    allowToSendMessage() && !isUploading
                                      ? 'bg-[var(--new-primary)]'
                                      : 'bg-[var(--channelButton)] cursor-not-allowed'
                                  }`}
                                  disabled={
                                    !allowToSendMessage() || isUploading
                                  }
                                  onClick={() => {
                                    if (allowToSendMessage() && !isUploading) {
                                      processSendQueue();
                                    }
                                  }}
                                >
                                  {isMsgSending ? (
                                    <Loader
                                      Width={20}
                                      Height={20}
                                      loaderBg="white"
                                    />
                                  ) : (
                                    <>
                                      <SendInbox
                                        color={
                                          allowToSendMessage()
                                            ? 'white'
                                            : 'var(--font-600)'
                                        }
                                        width={'1vw'}
                                        height={'1vw'}
                                      />
                                      Send
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          </Drawer>
                        </div>
                      )}
                      <div className="flex items-center justify-between rounded-b-[0.7vw]">
                        <div className="flex items-center gap-1">
                          <div className="w-auto flex items-center gap-[0.5vw] border-r-[0.08vw] border-[var(--border-50)] pr-[1vw]">
                            <button
                              className={cn(
                                'flex items-center gap-[0.3vw] text-[rgba(135,150,175,1)] var(--font-600) rounded-[0.3vw] p-1 transition-colors duration-200',
                                canSendNotes
                                  ? 'hover:bg-[#ececeb] cursor-pointer'
                                  : 'cursor-not-allowed',
                              )}
                              onClick={() => {
                                if (canSendNotes) {
                                  handleChat();
                                }
                              }}
                            >
                              <div
                                className={cn(
                                  'flex items-center gap-[0.3vw]',
                                  !canSendNotes && 'opacity-50',
                                )}
                              >
                                <InboxNoteIcon />
                                <span className="text-[rgba(135,150,175,1)] font14px-400">
                                  Note
                                </span>
                              </div>
                              {!canSendNotes && (
                                <UpgradeBadge iconOnly={true} />
                              )}
                            </button>
                          </div>
                          {chatToggle && (
                            <div className="flex items-center">
                              <div className="w-auto h-fit flex items-center gap-[0.5vw]">
                                <button className="size-[1.5vw] hover:bg-slate-100 flex items-center justify-center rounded-[0.4vw] cursor-not-allowed">
                                  <img
                                    src={ICONS?.StarGroup}
                                    alt=""
                                    className="size-[0.8vw]"
                                  />
                                </button>
                                <button
                                  className="size-[1.5vw] hover:bg-slate-100 flex items-center justify-center rounded-[0.4vw]"
                                  onClick={() => {
                                    setOpenTemplateList(true);
                                  }}
                                >
                                  <img
                                    src={ICONS?.SelectTemplate}
                                    alt=""
                                    className="size-[0.9vw]"
                                  />
                                </button>
                              </div>
                              <div className="w-auto h-fit flex items-center gap-[0.5vw] border-x-[0.08vw] border-[var(--border-50)] px-[1vw] mx-[0.8vw]">
                                <InlineStyleControls
                                  options={INLINE_STYLES}
                                  editorState={editorState}
                                  onToggle={handleToolbarClick}
                                />
                              </div>
                              <div className="w-auto h-fit flex items-center gap-[0.5vw] border-r-[0.08vw] border-[var(--border-50)] pr-[1vw] relative">
                                {FEATURE_FLAG_SEND_ATTACHMENTS && (
                                  <Popover
                                    trigger={['click']}
                                    open={openAttachment && !fileOpen}
                                    onOpenChange={(open) => {
                                      setOpenAttachment(open);
                                    }}
                                    arrow={false}
                                    content={renderAttachmentMenu(() =>
                                      setOpenAttachment(false),
                                    )}
                                  >
                                    <button
                                      className={`size-[1.5vw] hover:bg-slate-100 flex items-center justify-center rounded-[0.4vw] ${
                                        fileOpen
                                          ? 'opacity-50 cursor-not-allowed'
                                          : ''
                                      }`}
                                    >
                                      <img
                                        src={ICONS?.Attachments}
                                        alt=""
                                        className="size-[1vw]"
                                      />
                                    </button>
                                  </Popover>
                                )}
                                <Popover
                                  trigger={['click']}
                                  open={showEmojiPicker}
                                  onOpenChange={(open) =>
                                    setShowEmojiPicker(open)
                                  }
                                  arrow={false}
                                  content={
                                    <Picker
                                      data={data}
                                      onEmojiSelect={handleEmojiClick}
                                      searchPosition="none"
                                      previewPosition="none"
                                      width={`5vw`}
                                    />
                                  }
                                >
                                  <button
                                    className="size-[1.5vw] flex items-center justify-center rounded-[0.4vw] p-[0.5vw] hover:bg-slate-100"
                                    type="button"
                                    onClick={() =>
                                      setShowEmojiPicker(!showEmojiPicker)
                                    }
                                  >
                                    <img
                                      src={ICONS?.step3EmojiIcon}
                                      alt="emoji"
                                      className="size-[1vw]"
                                    />
                                  </button>
                                </Popover>
                              </div>
                              <div className="w-auto h-fit flex items-center gap-[0.5vw] border-[var(--border-50)] relative ml-1">
                                <Popover
                                  trigger={['click']}
                                  open={
                                    canUseQuickReply && showQuickRepliesManager
                                  }
                                  onOpenChange={(open) => {
                                    if (canUseQuickReply) {
                                      setShowQuickRepliesManager(open);
                                    }
                                  }}
                                  arrow={false}
                                  content={
                                    <QuickReplyPanel
                                      quickReplies={quickReplies}
                                      setQuickReplies={setQuickReplies}
                                      loading={quickRepliesLoading}
                                      setLoading={setQuickRepliesLoading}
                                      setShowQuickRepliesManager={
                                        setShowQuickRepliesManager
                                      }
                                      currentBrand={currentBrand}
                                      selectedMessage={handleSelectedMessage}
                                    />
                                  }
                                >
                                  <button
                                    className={cn(
                                      'p-[0.1vw] flex items-center p-[5px] shrink-1 justify-center rounded rounded-md text-gray-500 transition-all duration-300 gap-1',
                                      !canUseQuickReply
                                        ? 'cursor-not-allowed'
                                        : showQuickRepliesManager
                                        ? 'bg-[rgba(231,224,253,1)] text-[rgba(105,64,242,1)]'
                                        : 'hover:bg-slate-100 cursor-pointer',
                                    )}
                                    onClick={() => {
                                      if (canUseQuickReply) {
                                        !showQuickRepliesManager &&
                                          fetchQuickReplies();
                                      }
                                    }}
                                  >
                                    <LightningIcon
                                      svgClassName={cn(
                                        'size-[0.8vw]',
                                        !canUseQuickReply && 'opacity-50',
                                      )}
                                    />
                                    {!canUseQuickReply && (
                                      <UpgradeBadge iconOnly={true} />
                                    )}
                                  </button>
                                </Popover>
                              </div>
                            </div>
                          )}
                        </div>

                        {!isUploading && attachments?.length === 0 && (
                          <div className="flex items-center">
                            <button
                              className={`  flex-row align-center rounded-[0.37vw] ${
                                !isMsgSending && allowToSendMessage()
                                  ? 'cursor-pointer bg-[var(--new-primary)] '
                                  : 'cursor-not-allowed bg-[var(--channelButton)] '
                              }`}
                              onClick={() => {
                                if (!isMsgSending && allowToSendMessage()) {
                                  processSendQueue();
                                }
                              }}
                              // disabled={item?.agent_engagement_status}
                            >
                              {isMsgSending ? (
                                <Loader
                                  Width={20}
                                  Height={20}
                                  loaderBg="white"
                                />
                              ) : (
                                <div className="pr-10 flex-row align-center">
                                  <span
                                    className={`px-2.5 py-1  ${
                                      allowToSendMessage()
                                        ? 'text-[white]'
                                        : ' text-[var(--font-600)]'
                                    }`}
                                  >
                                    Send
                                  </span>

                                  <SendInbox
                                    color={
                                      allowToSendMessage()
                                        ? 'white'
                                        : 'var(--font-600)'
                                    }
                                    width={'1vw'}
                                    height={'1vw'}
                                  />
                                </div>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <NoteEditor
                      noteContent={noteContent}
                      setNoteContent={setNoteContent}
                      handleChat={handleChat}
                      showEmojiPickerCaption={showEmojiPickerCaption}
                      setShowEmojiPickerCaption={setShowEmojiPickerCaption}
                      data={data}
                      assignGetData={assignGetData}
                      textareaRef={textareaRef}
                      handleNoteEmojiClick={handleNoteEmojiClick}
                      noteUpdate={noteUpdate}
                      noteCreate={noteCreate}
                      isNoteLoading={isNoteLoading}
                    />
                  )}
                </>
              )}
            </>
          )}
        </div>

        <input
          type="file"
          accept=".jpg,.jpeg,.png"
          multiple
          onChange={(e) => {
            handleUploadFiles(e, 'image');
          }}
          ref={fileInputRef1}
          hidden
        />
        <input
          type="file"
          accept=".mp4"
          multiple
          onChange={(e) => {
            handleUploadFiles(e, 'video');
          }}
          ref={fileInputRef2}
          hidden
        />
        <input
          type="file"
          accept=".pdf"
          multiple
          onChange={(e) => {
            handleUploadFiles(e, 'document');
          }}
          ref={fileInputRef3}
          hidden
        />
        <input
          type="file"
          accept=".aac,.amr,.mp3,.m4a,.ogg"
          multiple
          onChange={(e) => {
            handleUploadFiles(e, 'audio');
          }}
          ref={fileInputRef4}
          hidden
        />

        {showList && (
          <Drawer
            placement="bottom"
            onClose={onCloseShowList}
            open={showList}
            closable={false}
            rootClassName="message-input-drawer w-full absolute !bottom-0 !left-[70%]"
            className="overflow-hidden !rounded-t-[80px]"
            onOpenChange={(open) => {
              onCloseShowList(open);
            }}
          >
            <div className="">
              <div
                className="w-1/5 bg-gray-400 h-2 contents-[''] rounded-full mx-auto mt-10 cursor-pointer"
                onClick={() => onCloseShowList()}
              ></div>
              <div className="pt-8 flex flex-col gap-5">
                {selectedMessage?.data?.interactive?.action?.sections?.map(
                  (sec) => {
                    return (
                      <div className="py-2">
                        <h5 className="text-base weight-bold pb-2.5">
                          {sec?.title}
                        </h5>
                        <div className="flex flex-col px-5 gap-2.5">
                          {sec?.rows?.map((row) => {
                            return (
                              <div className="w-full h-7 flex items-center justify-between">
                                <p>{row.title}</p>
                                <p className="size-3.5 rounded-full border border-[var(--border-200)]"></p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
            </div>
          </Drawer>
        )}

        <Modal
          open={openTemplateList}
          trigger={['click']}
          afterOpenChange={(open) => {
            setOpenTemplateList(open);
          }}
          styles={{ content: { padding: 0 } }}
          centered
          footer={null}
          closable={false}
          mask={true}
          wrapClassName={'bg-[#00000095]'}
          onCancel={handleCancel}
          destroyOnClose
          width={'82vw'}
          style={{
            width: '100%',
            borderRadius: 20,
            boxShadow: '0px 10px 24px 0px rgba(79, 92, 128, 0.15)',
          }}
        >
          <Header
            title="Templates"
            titleSize="largest"
            rootStyle="!px-[1.5vw] !py-[1vw] gap-2"
            handleCancel={handleCancel}
            className="new-header"
            prefix={ICONS?.templateInoboxIcon}
          />

          <TemplateListContainer
            className="h-[58vh]"
            isSending={isSending}
            setIsSending={setIsSending}
            changeMedia={FEATURE_FLAG_TEMPLATE_CHANGE_MEDIA}
            changeVariable={FEATURE_FLAG_TEMPLATE_CHANGE_VARIABLE}
            sendTemplate={(data) => {
              const {
                bodyVariables,
                headerVariable,
                message_template_id,
                category,
                subcategory_type,
                payment_details,
                ...rest
              } = data;

              let variables = [
                ...new Set([...bodyVariables, ...headerVariable]),
              ];

              variables = Object.fromEntries(
                variables.map((item) => [
                  item?.title?.replace(/{{|}}/g, ''),
                  item?.value,
                ]),
              );

              sendTemplate({
                sendFrom: !activeTabItem?.send_from_phone_number_id
                  ? selectedPhoneNumber?.send_from_phone_number_id
                  : activeTabItem?.send_from_phone_number_id,
                sendTo: selectedPerson?.to_number,
                metaTemplateId: message_template_id,
                brandId: currentBrand?.brand_id,
                subcategory_type: subcategory_type,
                category: category,
                mediaUrl: rest,
                variables: variables,
                payment_details: payment_details,
              });
            }}
          />
        </Modal>
      </>
    );
  },
);

export default MessageInput;
